import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause, Download, Headphones, Volume2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ListenPage = () => {
  const { installationId } = useParams();
  const audioRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [installation, setInstallation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    fetchInstallation();
  }, [installationId]);

  const fetchInstallation = async () => {
    try {
      const res = await fetch(`${API_URL}/api/voice-studio/share/${installationId}`);
      if (!res.ok) {
        throw new Error("Installation not found");
      }
      const data = await res.json();
      setInstallation(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePlayback = async () => {
    if (!audioRef.current || !installation) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        audioRef.current.src = installation.audio_url;
        audioRef.current.load();
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (err) {
        console.log("Playback error:", err.message);
      }
    }
  };

  const handleDownload = () => {
    if (!installation?.audio_url) return;
    
    const link = document.createElement("a");
    link.href = installation.audio_url;
    link.download = `${installation.title || "installation"}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#2D4A2A] to-[#1A2D18] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !installation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#2D4A2A] to-[#1A2D18] flex items-center justify-center p-6">
        <div className="text-center text-white">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Volume2 size={40} className="text-white/50" />
          </div>
          <h1 className="font-heading text-2xl mb-2">Installation Not Found</h1>
          <p className="text-white/60">This meditation may have been removed or the link is invalid.</p>
          <a 
            href="/" 
            className="inline-block mt-6 px-6 py-3 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors"
          >
            Visit Reclaim
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#2D4A2A] to-[#1A2D18] flex flex-col">
      {/* Subtle ambient animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.1, 0.3, 0.1],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 text-center relative z-10"
      >
        <img 
          src="/assets/reclaim-logo-white.png" 
          alt="Reclaim" 
          className="h-8 mx-auto mb-2"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <p className="text-white/60 text-sm tracking-widest uppercase">School of Consciousness</p>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Voice Studio Badge */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-8"
          >
            <span className="inline-block px-4 py-1.5 bg-[#D4A373]/20 rounded-full text-[#D4A373] text-sm font-medium">
              Voice Studio
            </span>
          </motion.div>

          {/* Affirmation Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center mb-8 border border-white/5"
          >
            <motion.div 
              className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6"
              animate={isPlaying ? { 
                scale: [1, 1.1, 1],
                boxShadow: ['0 0 0 0 rgba(255,255,255,0)', '0 0 20px 5px rgba(255,255,255,0.2)', '0 0 0 0 rgba(255,255,255,0)']
              } : {}}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Volume2 size={28} className="text-white" />
            </motion.div>
            
            <p className="font-heading text-2xl text-white italic leading-relaxed mb-6">
              "{installation.affirmation_text}"
            </p>

            {/* Creator */}
            <p className="text-white/50 text-sm">
              Created by {installation.creator_name}
            </p>
          </motion.div>

          {/* Details */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap justify-center gap-3 mb-8"
          >
            <span className="px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-sm">
              {installation.voice_style_name}
            </span>
            {installation.background_audio_name !== "Silence" && (
              <span className="px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-sm">
                {installation.background_audio_name}
              </span>
            )}
            {installation.binaural_beat !== "none" && (
              <span className="px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-sm">
                {installation.binaural_beat_name}
              </span>
            )}
          </motion.div>

          {/* Binaural Notice */}
          {installation.binaural_beat !== "none" && (
            <div className="flex items-center justify-center gap-2 mb-6 text-[#D4A373]">
              <Headphones size={18} />
              <span className="text-sm">Use headphones for best experience</span>
            </div>
          )}

          {/* Play Controls */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={togglePlayback}
              className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-[#2D4A2A] shadow-2xl hover:scale-105 transition-transform"
              data-testid="play-shared-installation"
            >
              {isPlaying ? <Pause size={36} /> : <Play size={36} className="ml-1" />}
            </button>
          </div>

          {/* Download */}
          <div className="text-center mt-8">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
            >
              <Download size={18} />
              Download
            </button>
          </div>
        </motion.div>
      </div>

      {/* Footer CTA */}
      <div className="p-6 text-center border-t border-white/10">
        <p className="text-white/60 text-sm mb-3">Create your own voice installation</p>
        <a 
          href="/"
          className="inline-block px-6 py-3 bg-white text-[#2D4A2A] font-medium rounded-full hover:bg-white/90 transition-colors"
        >
          Try Reclaim Free
        </a>
      </div>

      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setIsPlaying(false)}
        preload="none"
      />
    </div>
  );
};

export default ListenPage;
